// W (watchlists) and WEI (world equity indices).

import { clear, h } from "../dom";
import { quoteMonitor, type Row } from "../monitor";
import { register, type Ctx } from "../registry";
import { resolve } from "../securities";

// --- W ----------------------------------------------------------------------

const WSTORE = "berg.watch";
interface Lists {
  current: string;
  lists: Record<string, string[]>;
}
const DEFAULT_LISTS: Lists = {
  current: "MAIN",
  lists: {
    MAIN: ["AAPL", "MSFT", "NVDA", "AMZN", "GOOGL", "META", "TSLA", "AVGO", "BRK-B", "JPM", "LLY", "V", "XOM", "WMT", "COST"],
    ETFS: ["SPY", "QQQ", "IWM", "DIA", "TLT", "HYG", "GLD", "SLV", "USO", "XLE", "XLF", "XLK", "SMH", "EEM", "EFA"],
  },
};

function loadLists(): Lists {
  try {
    const l = JSON.parse(localStorage.getItem(WSTORE) ?? "") as Lists;
    if (l.lists && Object.keys(l.lists).length) return l;
  } catch {
    // first use
  }
  return structuredClone(DEFAULT_LISTS);
}

function runW(ctx: Ctx): void {
  const lists = loadLists();
  const save = () => localStorage.setItem(WSTORE, JSON.stringify(lists));
  const [verb, ...rest] = ctx.args;
  if (verb === "NEW" && rest[0]) {
    lists.lists[rest[0]] ??= [];
    lists.current = rest[0];
  } else if (verb && lists.lists[verb]) {
    lists.current = verb;
  }
  if (!lists.lists[lists.current]) lists.current = Object.keys(lists.lists)[0];
  const syms = () => lists.lists[lists.current];
  if (verb === "ADD" || verb === "DEL") {
    for (const t of rest) {
      const s = resolve([t]);
      if (!s) continue;
      const l = syms();
      if (verb === "ADD" && !l.includes(s)) l.push(s);
      if (verb === "DEL") lists.lists[lists.current] = l.filter((x) => x !== s);
    }
  }
  save();

  const rows = (): Row[] => syms().map((sym) => ({ sym }));
  const toolbar = h("div", { class: "toolbar" });
  const mon = quoteMonitor(ctx, rows(), {
    cols: ["last", "chg", "pct", "volume", "mcap", "pe", "spark", "range", "time"],
    extra: [
      {
        label: "",
        get: () => 0,
        sort: false,
        fmt: (_v, i) =>
          h(
            "button",
            {
              title: "Remove",
              onclick: () => {
                lists.lists[lists.current] = syms().filter((s) => s !== i.row.sym);
                save();
                mon.setRows(rows());
              },
            },
            "×",
          ),
      },
    ],
  });

  const add = h("input", { placeholder: "add tickers…", size: "16", spellcheck: "false" });
  add.addEventListener("keydown", (e) => {
    if (e.key !== "Enter") return;
    e.stopPropagation();
    for (const t of add.value.toUpperCase().split(/[\s,]+/).filter(Boolean)) {
      const s = resolve([t]);
      if (s && !syms().includes(s)) syms().push(s);
    }
    add.value = "";
    save();
    mon.setRows(rows());
  });
  const drawToolbar = () => {
    const select = h("select", null, ...Object.keys(lists.lists).map((n) => h("option", { value: n, selected: n === lists.current ? "" : undefined }, n)));
    select.addEventListener("change", () => {
      lists.current = select.value;
      save();
      mon.setRows(rows());
    });
    clear(
      toolbar,
      h("label", null, "List "),
      select,
      add,
      h(
        "button",
        {
          onclick: () => {
            const name = prompt("New list name")?.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, "");
            if (!name) return;
            lists.lists[name] ??= [];
            lists.current = name;
            save();
            drawToolbar();
            mon.setRows(rows());
          },
        },
        "New list",
      ),
      h(
        "button",
        {
          onclick: () => {
            if (Object.keys(lists.lists).length < 2 || !confirm(`Delete list ${lists.current}?`)) return;
            delete lists.lists[lists.current];
            lists.current = Object.keys(lists.lists)[0];
            save();
            drawToolbar();
            mon.setRows(rows());
          },
        },
        "Delete list",
      ),
      h("span", { class: "dim" }, "  W ADD TSLA · W DEL TSLA · W <list> — saved in this browser"),
    );
  };
  drawToolbar();
  clear(ctx.body, toolbar, mon.el);
}

// --- WEI --------------------------------------------------------------------

const WEI_ROWS: Row[] = [
  { sym: "", group: "Americas" },
  { sym: "^DJI" },
  { sym: "^GSPC" },
  { sym: "^IXIC" },
  { sym: "^NDX" },
  { sym: "^RUT" },
  { sym: "^VIX" },
  { sym: "^GSPTSE" },
  { sym: "^MXX" },
  { sym: "^BVSP" },
  { sym: "", group: "EMEA" },
  { sym: "^STOXX50E" },
  { sym: "^STOXX" },
  { sym: "^FTSE" },
  { sym: "^GDAXI" },
  { sym: "^FCHI" },
  { sym: "^IBEX" },
  { sym: "FTSEMIB.MI" },
  { sym: "^AEX" },
  { sym: "^SSMI" },
  { sym: "", group: "Asia/Pacific" },
  { sym: "^N225" },
  { sym: "^HSI" },
  { sym: "000001.SS" },
  { sym: "^KS11" },
  { sym: "^TWII" },
  { sym: "^AXJO" },
  { sym: "^BSESN" },
  { sym: "^NSEI" },
  { sym: "^STI" },
  { sym: "", group: "US futures" },
  { sym: "ES=F" },
  { sym: "NQ=F" },
  { sym: "YM=F" },
  { sym: "RTY=F" },
];

function runWEI(ctx: Ctx): void {
  const mon = quoteMonitor(ctx, WEI_ROWS, { cols: ["last", "chg", "pct", "ytd", "spark", "time"], action: (s) => `${s} GP` });
  clear(ctx.body, mon.el);
}

register(
  {
    code: "W",
    name: "Watchlist Monitor",
    group: "Tools",
    needsSec: false,
    help: "Your watchlists with live quotes. W ADD TSLA, W DEL TSLA, W NEW TECH.",
    aliases: ["MON", "WATCH"],
    run: runW,
  },
  {
    code: "WEI",
    name: "World Equity Indices",
    group: "Markets",
    needsSec: false,
    help: "Major equity indices by region with YTD and intraday sparklines.",
    run: runWEI,
  },
);
